import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Lock, Crown, Zap, ArrowRight, X } from 'lucide-react'
import { useSubscription } from '../contexts/SubscriptionContext'

/**
 * Paywall Component
 * Shows upgrade prompt when user tries to access premium features
 *
 * Usage:
 * <Paywall feature="aiSearch" onClose={() => setShowPaywall(false)}>
 *   <PremiumFeatureComponent />
 * </Paywall>
 */
const Paywall = ({
  feature,
  onClose,
  children,
  title,
  description,
  inline = false
}) => {
  const navigate = useNavigate()
  const { isPremium, premiumFeatures } = useSubscription()

  // If user is premium, render children directly
  if (isPremium()) {
    return children || null
  }

  const featureInfo = {
    aiSearch: {
      title: 'AI-Powered Search',
      description: 'Get instant answers to any survival question with our intelligent AI search.',
      icon: Zap,
      benefits: [
        'Ask any survival question',
        'Get detailed, actionable answers',
        'Powered by advanced AI'
      ]
    },
    familySync: {
      title: 'Family Sync',
      description: 'Connect with family members, compete in challenges, and track progress together.',
      icon: Crown,
      benefits: [
        'Create family groups',
        'Weekly challenges & leaderboards',
        'Share achievements'
      ]
    },
    offlineMode: {
      title: 'Offline Mode',
      description: 'Download lessons for offline access - crucial during actual emergencies.',
      icon: Lock,
      benefits: [
        'Download any lesson',
        'Access without internet',
        'Auto-sync when online'
      ]
    },
    allPaths: {
      title: 'All Disaster Paths',
      description: 'Unlock all 5 disaster paths with 250+ lessons for complete preparedness.',
      icon: Crown,
      benefits: [
        'Pandemic Response (50 lessons)',
        'Grid Down Survival (50 lessons)',
        'Civil Unrest Safety (50 lessons)',
        'Economic Crisis (50 lessons)'
      ]
    },
    certificates: {
      title: 'Completion Certificates',
      description: 'Earn official certificates to showcase your survival skills.',
      icon: Crown,
      benefits: [
        'Shareable certificates',
        'Track your mastery',
        'Prove your expertise'
      ]
    }
  }

  const info = featureInfo[feature] || {
    title: title || 'Premium Feature',
    description: description || 'Upgrade to Premium to unlock this feature.',
    icon: Lock,
    benefits: ['Full access to all features', 'Priority support', 'Regular updates']
  }

  const IconComponent = info.icon

  // Inline paywall (for embedding in cards)
  if (inline) {
    return (
      <div className="paywall-inline">
        <div className="paywall-inline-content">
          <Lock className="w-8 h-8 text-orange-500 mb-2" />
          <h3 className="text-lg font-bold mb-1">{info.title}</h3>
          <p className="text-sm text-gray-400 mb-3">{info.description}</p>
          <button
            onClick={() => navigate('/pricing')}
            className="btn btn-primary btn-sm"
          >
            <Crown className="w-4 h-4" />
            Upgrade to Premium
          </button>
        </div>

        <style jsx>{`
          .paywall-inline {
            background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
            border: 1px solid rgba(249, 115, 22, 0.3);
            border-radius: 1rem;
            padding: 2rem;
            text-align: center;
          }

          .btn-sm {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    )
  }

  // Modal paywall
  return (
    <div className="paywall-overlay">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="paywall-modal"
      >
        {onClose && (
          <button className="paywall-close" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="paywall-header">
          <div className="paywall-icon">
            <IconComponent className="w-8 h-8" />
          </div>
          <h2 className="paywall-title">{info.title}</h2>
          <p className="paywall-description">{info.description}</p>
        </div>

        <div className="paywall-benefits">
          <h3>What you'll get:</h3>
          <ul>
            {info.benefits.map((benefit, index) => (
              <li key={index}>
                <Zap className="w-4 h-4 text-orange-500" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="paywall-pricing">
          <div className="price-tag">
            <span className="price">$39.99</span>
            <span className="period">/year</span>
          </div>
          <p className="price-note">Just $3.33/month • Cancel anytime</p>
        </div>

        <div className="paywall-actions">
          <button
            onClick={() => navigate('/pricing')}
            className="btn btn-primary btn-lg"
          >
            <Crown className="w-5 h-5" />
            Upgrade to Premium
            <ArrowRight className="w-5 h-5" />
          </button>
          {onClose && (
            <button onClick={onClose} className="btn btn-ghost">
              Maybe later
            </button>
          )}
        </div>

        <style jsx>{`
          .paywall-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100;
            padding: 1rem;
          }

          .paywall-modal {
            background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
            border: 1px solid rgba(249, 115, 22, 0.3);
            border-radius: 1.5rem;
            padding: 2.5rem;
            max-width: 420px;
            width: 100%;
            position: relative;
            text-align: center;
          }

          .paywall-close {
            position: absolute;
            top: 1rem;
            right: 1rem;
            background: rgba(255, 255, 255, 0.1);
            border: none;
            border-radius: 0.5rem;
            padding: 0.5rem;
            cursor: pointer;
            color: rgba(255, 255, 255, 0.6);
          }

          .paywall-close:hover {
            background: rgba(255, 255, 255, 0.2);
            color: white;
          }

          .paywall-header {
            margin-bottom: 1.5rem;
          }

          .paywall-icon {
            width: 64px;
            height: 64px;
            background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
            border-radius: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 1rem;
            color: white;
          }

          .paywall-title {
            font-size: 1.5rem;
            font-weight: 700;
            margin-bottom: 0.5rem;
          }

          .paywall-description {
            color: rgba(255, 255, 255, 0.7);
            font-size: 0.875rem;
            line-height: 1.5;
          }

          .paywall-benefits {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 0.75rem;
            padding: 1.25rem;
            margin-bottom: 1.5rem;
            text-align: left;
          }

          .paywall-benefits h3 {
            font-size: 0.875rem;
            font-weight: 600;
            margin-bottom: 0.75rem;
            color: rgba(255, 255, 255, 0.9);
          }

          .paywall-benefits ul {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .paywall-benefits li {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.875rem;
            color: rgba(255, 255, 255, 0.8);
          }

          .paywall-pricing {
            margin-bottom: 1.5rem;
          }

          .price-tag {
            display: flex;
            align-items: baseline;
            justify-content: center;
            gap: 0.25rem;
          }

          .price {
            font-size: 2.5rem;
            font-weight: 800;
            color: #f97316;
          }

          .period {
            font-size: 1rem;
            color: rgba(255, 255, 255, 0.6);
          }

          .price-note {
            font-size: 0.75rem;
            color: rgba(255, 255, 255, 0.5);
            margin-top: 0.25rem;
          }

          .paywall-actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .btn-lg {
            padding: 1rem 1.5rem;
            font-size: 1rem;
            font-weight: 600;
          }

          .btn-ghost {
            background: transparent;
            color: rgba(255, 255, 255, 0.6);
            border: none;
            cursor: pointer;
            font-size: 0.875rem;
          }

          .btn-ghost:hover {
            color: white;
          }
        `}</style>
      </motion.div>
    </div>
  )
}

export default Paywall
